import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Mail, MessageCircle, Send, UserCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * SendMessageDialog — dialog riutilizzabile per inviare un messaggio al cliente
 * via Email e/o WhatsApp usando template lookup personalizzabili.
 *
 * Props:
 *  - open, onClose
 *  - clientId: string (obbligatorio)
 *  - clientLabel: string (mostrato nell'header, es. "Mario Rossi")
 *  - policyId?: string — se valorizzato, i placeholder polizza vengono compilati
 *  - policyLabel?: string — mostrato per contesto
 *  - templateListKey: "client_message_templates" | "policy_message_templates" | ...
 *  - title, subtitle?: string — customizzano l'header
 *  - defaultTemplateCode?: string
 *  - defaultSubject?: string
 *  - placeholdersHint?: JSX/string
 */
export function SendMessageDialog({
  open,
  onClose,
  clientId,
  clientLabel,
  policyId = null,
  policyLabel = "",
  templateListKey,
  title = "Invia messaggio al cliente",
  subtitle = "Invia un messaggio via email e/o WhatsApp usando un template.",
  defaultTemplateCode = "",
  defaultSubject = "",
  placeholdersHint = null,
}) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    channel: "both",
    template_code: defaultTemplateCode || "",
    custom_text: "",
    subject: defaultSubject || "",
    to_email: "",
    to_mobile: "",
  });
  const [sending, setSending] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get(`/lookup-lists/${templateListKey}`);
      const list = Array.isArray(data) ? data : [];
      setTemplates(list);
      if (!form.template_code && list.length > 0) {
        setForm((f) => ({ ...f, template_code: list[0].code }));
      }
    } catch {
      setTemplates([]);
    }
  }, [templateListKey]);

  useEffect(() => {
    if (open) {
      loadTemplates();
      setForm({
        channel: "both",
        template_code: defaultTemplateCode || "",
        custom_text: "",
        subject: defaultSubject || "",
        to_email: "",
        to_mobile: "",
      });
    }
  }, [open, loadTemplates, defaultTemplateCode, defaultSubject]);

  const submit = async () => {
    if (!clientId) return toast.error("Cliente mancante");
    setSending(true);
    try {
      const payload = {
        channel: form.channel,
        template_list_key: templateListKey,
        template_code: form.custom_text?.trim() ? null : (form.template_code || null),
        custom_text: form.custom_text?.trim() || null,
        subject: form.subject?.trim() || null,
        policy_id: policyId || null,
        to_email: form.to_email?.trim() || null,
        to_mobile: form.to_mobile?.trim() || null,
      };
      const { data } = await api.post(`/clients/${clientId}/send-message`, payload);
      const waSent = (data.channels_sent || []).find((c) => c.channel === "whatsapp");
      if (waSent?.wa_url) {
        window.open(waSent.wa_url, "_blank", "noopener,noreferrer");
      }
      const emailSent = (data.channels_sent || []).find((c) => c.channel === "email");
      const msgs = [];
      if (emailSent) msgs.push(`Email → ${emailSent.to}`);
      if (waSent) msgs.push(`WhatsApp aperto per ${waSent.to}`);
      toast.success(msgs.length ? msgs.join(" · ") : "Messaggio inviato");
      if (data.channels_failed?.length) {
        data.channels_failed.forEach((f) => toast.error(`${f.channel}: ${f.reason}`));
      }
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const currentTemplate = templates.find((t) => t.code === form.template_code);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="send-message-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <UserCheck className="h-5 w-5" /> {title}
          </DialogTitle>
          <DialogDescription>
            {subtitle} {clientLabel && <><br/>Destinatario: <strong>{clientLabel}</strong></>}
            {policyLabel && <span className="ml-1 text-slate-500">· Polizza: <strong>{policyLabel}</strong></span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {placeholdersHint}
          <div>
            <Label className="text-xs">Canale</Label>
            <div className="flex items-center gap-2 mt-1">
              {["email", "whatsapp", "both"].map((ch) => (
                <label key={ch} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs cursor-pointer ${form.channel === ch ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 hover:bg-slate-50"}`}>
                  <input
                    type="radio"
                    name="msg-channel"
                    value={ch}
                    checked={form.channel === ch}
                    onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                    className="sr-only"
                    data-testid={`send-msg-channel-${ch}`}
                  />
                  {ch === "email" && <Mail className="h-3.5 w-3.5" />}
                  {ch === "whatsapp" && <MessageCircle className="h-3.5 w-3.5" />}
                  {ch === "both" && (<><Mail className="h-3.5 w-3.5" /><MessageCircle className="h-3.5 w-3.5" /></>)}
                  {ch === "email" ? "Email" : ch === "whatsapp" ? "WhatsApp" : "Entrambi"}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Template</Label>
            <Select
              value={form.template_code}
              onValueChange={(v) => setForm((f) => ({ ...f, template_code: v, custom_text: "" }))}
            >
              <SelectTrigger data-testid="send-msg-template"><SelectValue placeholder="Seleziona template…" /></SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? (
                  <SelectItem value="__none__">Nessun template disponibile</SelectItem>
                ) : (
                  templates.map((t) => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.code.charAt(0).toUpperCase() + t.code.slice(1).replace(/_/g, " ")}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {currentTemplate && !form.custom_text && (
              <p className="text-[11px] text-slate-500 mt-1.5 whitespace-pre-wrap italic border-l-2 border-slate-200 pl-2">
                {currentTemplate.label}
              </p>
            )}
            <p className="text-[10px] text-slate-400 mt-0.5">
              Puoi aggiungere/modificare i template da <strong>Impostazioni → Liste Personalizzabili</strong>.
            </p>
          </div>
          <div>
            <Label className="text-xs">Testo personalizzato (opzionale — sovrascrive il template)</Label>
            <Textarea
              rows={4}
              value={form.custom_text}
              onChange={(e) => setForm((f) => ({ ...f, custom_text: e.target.value }))}
              placeholder="Placeholder disponibili: {cliente}, {agenzia}, {telefono_agenzia}, {indirizzo_agenzia}, {polizza}, {compagnia}, {data_scadenza}, {tipo_polizza}"
              className="font-mono text-xs"
              data-testid="send-msg-custom-text"
            />
          </div>
          {(form.channel === "email" || form.channel === "both") && (
            <>
              <div>
                <Label className="text-xs">Oggetto email (opzionale)</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder="Oggetto della email"
                  data-testid="send-msg-subject"
                />
              </div>
              <div>
                <Label className="text-xs">Email destinatario (default: cliente)</Label>
                <Input
                  type="email"
                  value={form.to_email}
                  onChange={(e) => setForm((f) => ({ ...f, to_email: e.target.value }))}
                  placeholder="Vuoto = usa email del cliente"
                  data-testid="send-msg-to-email"
                />
              </div>
            </>
          )}
          {(form.channel === "whatsapp" || form.channel === "both") && (
            <div>
              <Label className="text-xs">Numero WhatsApp (default: cellulare del cliente)</Label>
              <Input
                value={form.to_mobile}
                onChange={(e) => setForm((f) => ({ ...f, to_mobile: e.target.value }))}
                placeholder="Vuoto = usa cellulare del cliente"
                data-testid="send-msg-to-mobile"
              />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={sending}>
              Annulla
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={sending}
              data-testid="send-msg-submit"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <Send className="h-4 w-4 mr-1" /> Invia
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SendMessageDialog;
